import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';

interface Props {
  accountId: string;
  accountName: string;
  accountEmail: string;
  onClose: () => void;
}

// Read-only signature viewer. Signatures are managed centrally by the office — a user can
// view their own signature here but can no longer edit it (editing was removed by request).
export default function SignatureModal({ accountId, accountName, accountEmail, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState('');

  useEffect(() => {
    let alive = true;
    api.getSignature(accountId)
      .then(({ signature }) => { if (alive) setSignature(signature || ''); })
      .catch(() => { })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ti ti-signature" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
              Email Signature
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{accountName} &lt;{accountEmail}&gt;</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        {/* Read-only notice */}
        <div style={{ padding: '10px 20px', background: 'var(--accent-bg)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--accent-text)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <i className="ti ti-lock" style={{ fontSize: 14, color: 'var(--accent)' }} aria-hidden />
          This signature is set centrally by the office and can&rsquo;t be edited here.
        </div>

        {/* Signature preview (read-only) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading
            ? <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Loading…</div>
            : signature
              ? <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, background: '#fff' }}>
                  <div dangerouslySetInnerHTML={{ __html: signature }} />
                </div>
              : <div style={{ color: 'var(--text-tertiary)', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>No signature set for this account.</div>
          }
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '7px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
